import { useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Skeleton,
    TextField,
    Typography,
} from "@mui/material";
import AppsIcon from '@mui/icons-material/Apps';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import { TagTypeIcon } from "../TagIcon";
import { formatLabel } from "../../utils/itemUtil";

export const DEFAULT_SHORTCUT_COUNT = 5;

// A few categories as shortcuts, always including the selected one. Order
// follows the API (tags are sorted by name).
export const pickShortcuts = (tags, selectedName, count = DEFAULT_SHORTCUT_COUNT) => {
    const shortcuts = tags.slice(0, count);
    if (selectedName && !shortcuts.some(t => t.name === selectedName)) {
        const selected = tags.find(t => t.name === selectedName);
        if (selected) shortcuts.splice(Math.max(count - 1, 0), 1, selected);
    }
    return shortcuts;
}

export const filterCategories = (tags, query) => {
    const q = query.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter(t => t.name.toLowerCase().includes(q) || formatLabel(t.name).toLowerCase().includes(q));
}

const CategoryButton = ({ tag, selected, onClick }) => (
    <Button
        variant="outlined"
        aria-pressed={selected}
        onClick={onClick}
        sx={{
            flexDirection: 'column',
            gap: '6px',
            py: '12px',
            px: '6px',
            minWidth: 0,
            color: selected ? 'sage.text' : 'text.primary',
            bgcolor: selected ? 'sage.surface' : 'background.paper',
            borderColor: selected ? 'sage.main' : 'divider',
            fontWeight: selected ? 650 : 500,
            lineHeight: 1.25,
            overflowWrap: 'anywhere',
            textAlign: 'center',
            '&:hover': {
                borderColor: 'sage.main',
                bgcolor: selected ? 'sage.surface' : 'background.paper',
            },
        }}
    >
        <TagTypeIcon name={tag.name} sx={{ fontSize: 22, color: selected ? 'sage.text' : 'sage.main' }} />
        {formatLabel(tag.name)}
    </Button>
);

// Category navigation that scales to any number of categories: shortcuts
// plus a searchable "All categories" picker.
export const CategoryPicker = ({ tags = [], status, error, selected, onSelect, onRetry, shortcutCount = DEFAULT_SHORTCUT_COUNT }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    const shortcuts = useMemo(() => pickShortcuts(tags, selected, shortcutCount), [tags, selected, shortcutCount]);
    const matches = useMemo(() => filterCategories(tags, query), [tags, query]);

    if (status === 'loading' || status === 'idle') {
        return (
            <Box aria-busy="true" aria-label="Loading categories" sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '9px' }}>
                {Array.from({ length: 6 }).map((_, ix) => <Skeleton key={ix} variant="rounded" height={72} />)}
            </Box>
        );
    }

    if (status === 'error') {
        return (
            <Alert severity="error" action={onRetry ? <Button color="inherit" size="small" onClick={onRetry}>Retry</Button> : null}>
                Categories couldn't be loaded.{error ? ` ${error}` : ''}
            </Alert>
        );
    }

    if (!tags.length) {
        return <Typography color="text.secondary">No categories are available yet.</Typography>;
    }

    const handleSelect = tag => {
        onSelect?.(tag);
        setOpen(false);
        setQuery('');
    }

    return (
        <>
            <Box
                role="group"
                aria-label="Item category"
                sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '9px' }}
            >
                {shortcuts.map(t => (
                    <CategoryButton key={t.id || t.name} tag={t} selected={t.name === selected} onClick={() => handleSelect(t)} />
                ))}
                {tags.length > shortcuts.length ?
                    <Button
                        variant="outlined"
                        onClick={() => setOpen(true)}
                        aria-haspopup="dialog"
                        sx={{ flexDirection: 'column', gap: '6px', py: '12px', px: '6px', minWidth: 0, color: 'sage.text', borderColor: 'divider', borderStyle: 'dashed', lineHeight: 1.25 }}
                    >
                        <AppsIcon aria-hidden="true" sx={{ fontSize: 22 }} />
                        All categories ({tags.length})
                    </Button>
                : null}
            </Box>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs" aria-labelledby="all-categories-title">
                <DialogTitle id="all-categories-title" sx={{ pr: 6 }}>
                    All categories
                    <IconButton aria-label="Close" onClick={() => setOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <Box sx={{ p: 2, pb: 1 }}>
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            placeholder="Search categories"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            inputProps={{ 'aria-label': 'Search categories', type: 'search' }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        />
                    </Box>
                    {matches.length ?
                        <List dense aria-label="Categories" sx={{ maxHeight: '55vh', overflowY: 'auto' }}>
                            {matches.map(t => (
                                <ListItemButton key={t.id || t.name} selected={t.name === selected} onClick={() => handleSelect(t)}>
                                    <ListItemIcon sx={{ minWidth: 34 }}><TagTypeIcon name={t.name} sx={{ fontSize: 20 }} /></ListItemIcon>
                                    <ListItemText primary={formatLabel(t.name)} primaryTypographyProps={{ sx: { overflowWrap: 'anywhere' } }} />
                                    {t.name === selected ? <CheckIcon fontSize="small" sx={{ color: 'sage.main' }} aria-label="Selected" /> : null}
                                </ListItemButton>
                            ))}
                        </List>
                        : <Typography color="text.secondary" sx={{ p: 2 }}>No categories match “{query}”.</Typography>
                    }
                </DialogContent>
            </Dialog>
        </>
    );
}
